import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  vinProcessorAPI,
  CreateJobRequest,
  GetJobsHistoryParams,
  ProcessVinsDirectRequest,
  JobLogs,
  ExecutionHistoryResponse,
  HealthStatus,
  ServiceInfo
} from '@/services/api/vinProcessor';

/**
 * Hook to get VIN processor service information
 */
export function useVINProcessorServiceInfo() {
  return useQuery<ServiceInfo>({
    queryKey: ['vinProcessing', 'serviceInfo'],
    queryFn: () => vinProcessorAPI.getServiceInfo(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1
  });
}

/**
 * Hook to check VIN processor health status
 */
export function useVINProcessorHealth() {
  return useQuery<HealthStatus>({
    queryKey: ['vinProcessing', 'health'],
    queryFn: () => vinProcessorAPI.getHealth(),
    refetchInterval: 30000, // Poll every 30 seconds
    retry: 1
  });
}

/**
 * Hook to create a VIN processing job
 */
export function useCreateVINJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateJobRequest) => vinProcessorAPI.createJob(request),
    retry: false,
    onSuccess: () => {
      // Invalidate jobs history to show the new job
      queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });
    }
  });
}

/**
 * Hook to get VIN job status
 * Automatically polls if job is running
 */
export function useVINJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['vinProcessing', 'jobStatus', jobId],
    queryFn: () => {
      if (!jobId) throw new Error('Job ID is required');
      return vinProcessorAPI.getJobStatus(jobId);
    },
    enabled: !!jobId,
    refetchInterval: query => {
      const data = query.state.data;
      // Poll every 5 seconds if job is running, otherwise don't poll
      if (data && (data.status === 'running' || data.status === 'pending')) {
        return 5000;
      }
      return false;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: 2
  });
}

/**
 * Hook to get execution history for VIN processing jobs
 * @param activeJobIds - Set of active job IDs to enable faster polling
 */
export function useVINJobsHistory(
  params?: GetJobsHistoryParams,
  activeJobIds?: Set<string>,
  options?: { enabled?: boolean }
) {
  return useQuery<ExecutionHistoryResponse>({
    queryKey: ['vinProcessing', 'history', params],
    queryFn: () => vinProcessorAPI.getJobsHistory(params),
    enabled: options?.enabled !== false, // Support lazy loading
    staleTime: 5 * 60 * 1000, // 5 minutes - increased for faster loads with cached data
    refetchInterval: () => {
      // Reactively check if there are active jobs (check current value, not initial value)
      const hasActiveJobs = activeJobIds && activeJobIds.size > 0;
      // If there are active jobs, refetch more frequently (every 10 seconds)
      if (hasActiveJobs) {
        return 10 * 1000;
      }
      // Otherwise, refetch every 2 minutes
      return 2 * 60 * 1000;
    },
    refetchOnWindowFocus: false, // Disable to prevent blocking on tab switch
    refetchOnMount: false, // Use cached data first for instant loads
    retry: 2
  });
}

/**
 * Hook to get detailed execution logs for a specific job
 * Returns comprehensive verbose logging including execution history, event logs,
 * storage locations, configuration, error logs, and output summaries.
 */
export function useVINJobLogs(jobId: string | null) {
  return useQuery<JobLogs>({
    queryKey: ['vinProcessing', 'jobLogs', jobId],
    queryFn: () => {
      if (!jobId) throw new Error('Job ID is required');
      return vinProcessorAPI.getJobLogs(jobId);
    },
    enabled: !!jobId,
    retry: 2,
    // Logs can be large, so cache for 1 minute
    staleTime: 60 * 1000
  });
}

/**
 * Hook to process VINs directly (synchronous)
 * Useful for testing or processing small batches (< 100 VINs)
 */
export function useProcessVinsDirect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ProcessVinsDirectRequest) => vinProcessorAPI.processVinsDirect(request),
    retry: false,
    onSuccess: () => {
      // Invalidate jobs history after direct processing
      queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });
    }
  });
}
